import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Alert,
  Chip,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  Slide,
  Fab,
  Snackbar
} from '@mui/material';
import {
  GetApp as GetAppIcon,
  Close as CloseIcon,
  Smartphone as SmartphoneIcon,
  DesktopWindows as DesktopIcon,
  CloudOff as OfflineIcon,
  Speed as SpeedIcon,
  Security as SecurityIcon,
  NotificationsActive as NotificationsIcon
} from '@mui/icons-material';
import { TransitionProps } from '@mui/material/transitions';

// Extend the Window interface to include the beforeinstallprompt event
declare global {
  interface Window {
    deferredPrompt: any;
  }
}

interface PWAInstallPromptProps {
  /** Show as a dialog instead of inline content */
  showAsDialog?: boolean;
  /** Show as a floating action button */
  showAsFab?: boolean;
  /** Show as a snackbar notification */
  showAsSnackbar?: boolean;
  /** Auto-show the prompt when available */
  autoShow?: boolean;
  /** Callback when install is completed */
  onInstalled?: () => void;
  /** Callback when install is dismissed */
  onDismissed?: () => void;
}

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export const PWAInstallPrompt: React.FC<PWAInstallPromptProps> = ({
  showAsDialog = false,
  showAsFab = false,
  showAsSnackbar = false,
  autoShow = true,
  onInstalled,
  onDismissed
}) => {
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [installationSupported, setInstallationSupported] = useState(false);
  const [platform, setPlatform] = useState<'desktop' | 'mobile' | 'unknown'>('unknown');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    // Detect platform
    const userAgent = navigator.userAgent;
    if (/Mobi|Android/i.test(userAgent)) {
      setPlatform('mobile');
    } else {
      setPlatform('desktop');
    }

    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone) {
      setIsInstalled(true);
      return;
    }

    // Handle beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: Event) => {
      console.log('beforeinstallprompt event fired');
      e.preventDefault();
      setDeferredPrompt(e);
      setInstallationSupported(true);
      
      if (autoShow && !localStorage.getItem('pwa-install-dismissed')) {
        setShowPrompt(true);
      }
    };

    // Handle app installed event
    const handleAppInstalled = () => {
      console.log('PWA was installed');
      setIsInstalled(true);
      setShowPrompt(false);
      setDeferredPrompt(null);
      onInstalled?.();
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [autoShow, onInstalled]);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      return;
    }

    setIsInstalling(true);

    try {
      // Show the install prompt
      const result = await deferredPrompt.prompt();
      console.log('Install prompt result:', result);

      // Wait for the user to respond to the prompt
      const choiceResult = await deferredPrompt.userChoice;
      console.log('User choice:', choiceResult);

      if (choiceResult.outcome === 'accepted') {
        console.log('User accepted the install prompt');
      } else {
        console.log('User dismissed the install prompt');
        localStorage.setItem('pwa-install-dismissed', 'true');
        onDismissed?.();
      }

      // Clear the deferredPrompt
      setDeferredPrompt(null);
      setShowPrompt(false);
    } catch (error) {
      console.error('Error during installation:', error);
    } finally {
      setIsInstalling(false);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-install-dismissed', 'true');
    onDismissed?.();
  };

  const benefits = [
    {
      icon: <OfflineIcon color="primary" />,
      title: 'Offline Access',
      description: 'Work even without internet connection'
    },
    {
      icon: <SpeedIcon color="primary" />,
      title: 'Faster Loading',
      description: 'Instant startup and smooth performance'
    },
    {
      icon: <SecurityIcon color="primary" />,
      title: 'Secure',
      description: 'Protected by HTTPS and app security'
    },
    {
      icon: <NotificationsIcon color="primary" />,
      title: 'Notifications',
      description: 'Get updates and reminders directly'
    }
  ];

  if (isInstalled || !installationSupported) {
    return null;
  }

  // Floating Action Button variant
  if (showAsFab && deferredPrompt) {
    return (
      <Fab
        color="primary"
        size={isMobile ? "large" : "medium"}
        onClick={handleInstallClick}
        disabled={isInstalling}
        sx={{
          position: 'fixed',
          bottom: isMobile ? 16 : 24,
          right: isMobile ? 16 : 24,
          zIndex: theme.zIndex.fab,
        }}
      >
        <GetAppIcon />
      </Fab>
    );
  }

  // Snackbar variant
  if (showAsSnackbar && showPrompt && deferredPrompt) {
    return (
      <Snackbar
        open={showPrompt}
        onClose={handleDismiss}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        autoHideDuration={10000}
      >
        <Alert
          severity="info"
          action={
            <Box display="flex" gap={1}>
              <Button
                color="inherit"
                size="small"
                onClick={handleInstallClick}
                disabled={isInstalling}
                startIcon={<GetAppIcon />}
              >
                Install
              </Button>
              <IconButton
                size="small"
                aria-label="close"
                color="inherit"
                onClick={handleDismiss}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          }
          sx={{ minWidth: isMobile ? '300px' : '400px' }}
        >
          Install Lighthouse app for faster access and offline use!
        </Alert>
      </Snackbar>
    );
  }

  // Dialog variant
  if (showAsDialog && showPrompt && deferredPrompt) {
    return (
      <Dialog
        open={showPrompt}
        onClose={handleDismiss}
        TransitionComponent={Transition}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : '16px',
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          paddingBottom: 2
        }}>
          <Box display="flex" alignItems="center" gap={1}>
            {platform === 'mobile' ? <SmartphoneIcon /> : <DesktopIcon />}
            <Typography variant="h6" component="div">
              Install Lighthouse
            </Typography>
          </Box>
          <IconButton
            aria-label="close"
            onClick={handleDismiss}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ paddingBottom: 3 }}>
          <Typography variant="body1" paragraph color="text.secondary">
            Get the full Lighthouse experience by installing our app. Enjoy faster loading, 
            offline access, and a native app-like experience on your {platform === 'mobile' ? 'phone' : 'desktop'}.
          </Typography>

          <Box sx={{ marginTop: 3, marginBottom: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ marginBottom: 2 }}>
              Why install?
            </Typography>
            
            <Box 
              display="grid" 
              gridTemplateColumns={isMobile ? '1fr' : 'repeat(2, 1fr)'} 
              gap={2}
            >
              {benefits.map((benefit, index) => (
                <Card key={index} variant="outlined" sx={{ borderRadius: '12px' }}>
                  <CardContent sx={{ padding: '16px !important' }}>
                    <Box display="flex" alignItems="flex-start" gap={1}>
                      <Box sx={{ flexShrink: 0 }}>
                        {benefit.icon}
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                          {benefit.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {benefit.description}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Box>

          <Box sx={{ marginTop: 2 }}>
            <Chip
              icon={platform === 'mobile' ? <SmartphoneIcon /> : <DesktopIcon />}
              label={`Optimized for ${platform === 'mobile' ? 'Mobile' : 'Desktop'}`}
              variant="outlined"
              size="small"
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ padding: '16px 24px 24px' }}>
          <Button 
            onClick={handleDismiss}
            color="primary"
            variant="outlined"
            fullWidth={isMobile}
          >
            Maybe Later
          </Button>
          <Button
            onClick={handleInstallClick}
            color="primary"
            variant="contained"
            disabled={isInstalling}
            startIcon={<GetAppIcon />}
            fullWidth={isMobile}
            sx={{ 
              marginLeft: isMobile ? 0 : 1,
              marginTop: isMobile ? 1 : 0
            }}
          >
            {isInstalling ? 'Installing...' : 'Install App'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // Inline card variant (default)
  if (showPrompt && deferredPrompt) {
    return (
      <Card 
        sx={{ 
          marginBottom: 2,
          borderRadius: '12px',
          border: `1px solid ${theme.palette.primary.main}`,
          background: `linear-gradient(135deg, ${theme.palette.primary.main}08 0%, ${theme.palette.primary.main}04 100%)`
        }}
      >
        <CardContent>
          <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={2}>
            <Box display="flex" alignItems="flex-start" gap={2} flex={1}>
              <GetAppIcon color="primary" sx={{ marginTop: 0.5 }} />
              <Box flex={1}>
                <Typography variant="h6" gutterBottom sx={{ marginBottom: 1 }}>
                  Install Lighthouse App
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph sx={{ marginBottom: 2 }}>
                  Add Lighthouse to your {platform === 'mobile' ? 'home screen' : 'desktop'} for 
                  faster access, offline support, and a better experience.
                </Typography>
                <Box display="flex" gap={1} flexWrap="wrap">
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleInstallClick}
                    disabled={isInstalling}
                    startIcon={<GetAppIcon />}
                  >
                    {isInstalling ? 'Installing...' : 'Install'}
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleDismiss}
                  >
                    Dismiss
                  </Button>
                </Box>
              </Box>
            </Box>
            <IconButton
              aria-label="close"
              onClick={handleDismiss}
              size="small"
              sx={{ marginTop: -1, marginRight: -1 }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return null;
};

export default PWAInstallPrompt;